import { WorkEmpty } from '../../components/work-page.tsx';
import { WorkPage } from '../../components/work-page.tsx';
import '../portal-page.css';
import { useQuery } from '@tanstack/react-query';
import { partnerApi } from '../../api/partner.ts';
import { Alert, LoadingBlock } from '../../components/ui.tsx';
import { useDocumentTitle } from '../../hooks/index.ts';
import { money, Pill, shortDate, TableShell } from './common.tsx';

function toneFor(status: string): 'good' | 'warn' | 'bad' | 'neutral' {
  if (status === 'paid') return 'good';
  if (status === 'failed' || status === 'cancelled' || status === 'partially_failed') return 'bad';
  if (status === 'held' || status === 'under_review') return 'warn';
  return 'neutral';
}

export default function PartnerPayoutsPage() {
  useDocumentTitle('Partner payouts');

  const payouts = useQuery({
    queryKey: ['partner', 'payouts'],
    queryFn: partnerApi.payouts,
  });

  return (
    <div className="portal-page">
      <WorkPage
        title="Payouts"
        description="Every payout raised against your balance. Anything under the minimum is carried forward rather than sent."
      >
        {payouts.isLoading ? <LoadingBlock label="Loading your payouts…" /> : null}
        {payouts.isError ? (
          <Alert tone="error" title="Your payouts could not be loaded">
            {payouts.error instanceof Error ? payouts.error.message : 'Please try again in a moment.'}
          </Alert>
        ) : null}
        {payouts.data && payouts.data.payouts.length === 0 ? (
          <WorkEmpty title="No payouts yet">
            A payout is raised on the monthly run once your finalized balance reaches the minimum.
          </WorkEmpty>
        ) : null}
        {payouts.data && payouts.data.payouts.length > 0 ? (
          <TableShell
            label="Payouts"
            head={
              <tr>
                <th scope="col" className="px-4 py-3 text-right font-semibold">
                  Amount
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Status
                </th>
                <th scope="col" className="px-4 py-3 text-right font-semibold">
                  Carried forward
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Sent
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Completed
                </th>
              </tr>
            }
          >
            {payouts.data.payouts.map((payout) => (
              <tr key={payout.id} className="border-b border-[color:var(--color-line)] last:border-b-0">
                <td className="px-4 py-3 text-right font-medium">{money(payout.amountCents, payout.currency)}</td>
                <td className="px-4 py-3">
                  <Pill value={payout.status} tone={toneFor(payout.status)} />
                  {/* A hold that does not say why is a support ticket. */}
                  {payout.holdReason ? (
                    <div className="table-text mt-1 max-w-64 text-xs text-[color:var(--color-muted)]">
                      {payout.holdReason}
                    </div>
                  ) : null}
                </td>
                <td className="px-4 py-3 text-right">
                  {payout.carriedForwardCents ? money(payout.carriedForwardCents, payout.currency) : '—'}
                </td>
                <td className="px-4 py-3">{shortDate(payout.sentAt)}</td>
                <td className="px-4 py-3">{shortDate(payout.completedAt)}</td>
              </tr>
            ))}
          </TableShell>
        ) : null}
      </WorkPage>
    </div>
  );
}
